/**
 * Registration Form Component
 * Material-UI based registration form with React Hook Form + Zod validation
 * Requirements: 6.1, 6.7
 */
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Link as MuiLink,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { registerSchema, type RegisterFormData } from '../schemas/auth.schema';
import { useAuth } from '../context/AuthContext';

export const RegisterForm: React.FC = () => {
  const { register: registerUser, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      clearError();
      await registerUser(data.email, data.password);
      navigate('/login', { state: { message: '登録が完了しました。ログインしてください。' } });
    } catch {
      // Error is handled by AuthContext
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        width: '100%',
        mx: 'auto',
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom align="center">
        アカウント登録
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('email')}
          label="メールアドレス"
          type="email"
          fullWidth
          margin="normal"
          error={!!errors.email}
          helperText={errors.email?.message}
          autoComplete="email"
          autoFocus
          disabled={isLoading}
        />

        <TextField
          {...register('password')}
          label="パスワード"
          type="password"
          fullWidth
          margin="normal"
          error={!!errors.password}
          helperText={errors.password?.message || 'パスワードは8文字以上で、大文字・小文字・数字を含む必要があります'}
          autoComplete="new-password"
          disabled={isLoading}
        />

        <TextField
          {...register('confirmPassword')}
          label="パスワード（確認）"
          type="password"
          fullWidth
          margin="normal"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          autoComplete="new-password"
          disabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : '登録'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <MuiLink component={Link} to="/login" variant="body2">
            すでにアカウントをお持ちですか？ログインはこちら
          </MuiLink>
        </Box>
      </Box>
    </Paper>
  );
};
